import { Component, ElementRef, ViewChild } from "@angular/core";
import {
    ConfiguratorNewsletterService,
    DatastoreDto,
    Newsletter,
    NewsletterNode,
    Partner,
} from "@tj/tj-dmp-lib-api";
import { ConfirmationService, MenuItem, MessageService, ToastMessageOptions } from "primeng/api";
import { ClipboardService } from "../../../services/clipboard.service";
import { TranslateService } from "@ngx-translate/core";
import { AccountService } from "../../../auth/account.service";
import { isAdminOfTenant } from "../../../auth/admin-levels";
import { SelectAffectedPartnersComponent } from "../../../components/select-affected-partners/select-affected-partners.component";

interface Subscription {
    data: NewsletterNode;
    children: Subscription[];
    expanded?: boolean;
    parent: Subscription;
}

@Component({
    selector: "dmp-subscriptions",
    templateUrl: "./subscriptions.component.html",
    styleUrls: ["./subscriptions.component.scss"],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class SubscriptionsComponent {
    breadcrumb = [
        { label: this.translate.instant('menu.marketing.label') },
        { label: this.translate.instant('menu.marketing.subscriptions') }
    ];
    description = this.translate.instant('subscriptions.description');
    datastore: DatastoreDto;
    partner: Partner;
    subscriptions = [] as Subscription[];
    selectedSubscriptions: Subscription[] = [];
    current: NewsletterNode;
    editable: Newsletter;
    currentOpened: Subscription;
    uploadingFile = false;
    exportMenuItems: MenuItem[] = [];
    isAdmin = false;
    sharingTarget: Newsletter;

    @ViewChild('newsletterFileInput') newsletterFileInput: ElementRef;
    @ViewChild(SelectAffectedPartnersComponent) affectedPartnersSettings: SelectAffectedPartnersComponent;

    get items(): MenuItem[] {
        const otherItems: MenuItem[] = [
            {
                label: this.translate.instant('subscriptions.copyId'),
                icon: "fas fa-copy",
                 permission: "SUBSCRIPTIONS.edit",
                command: () => {
                    const uuid = this.current.uuid;
                    this.clipboardService.copyToClipboard(uuid).then(() => {
                        this.messageService.add({
                            severity: "success",
                            summary: this.translate.instant('subscriptions.idCopiedSummary'),
                            detail: uuid,
                        });
                    });
                },
            },
            {
                label: this.translate.instant('subscriptions.addChild'),
                permission: "SUBSCRIPTIONS.create",
                icon: "fas fa-plus",
                command: () => {
                    const newsletter = {
                        children: [],
                        data: {
                            uuid: null,
                            name: "",
                            description: "",
                            channelEnabled: [],
                            children: [],
                        },
                        expanded: false,
                        parent: {
                            data: this.current,
                            parent: null,
                            children: [],
                            expanded: true,
                        },
                    };
                    const subscription = this.searchSubscriptionDeep(
                        this.subscriptions,
                        this.current.uuid
                    );

                    this.currentOpened = newsletter;

                    subscription.children.push(newsletter);
                    subscription.data.children.push(newsletter.data);
                    this.editable = newsletter.data as Newsletter;
                    subscription.expanded = true;
                    this.subscriptions = [...this.subscriptions];
                    // this.save();
                },
            },
        ];

        // Le partage et la duplication ne s'appliquent qu'aux abonnements racine (ceux qui ont un id : entité Newsletter),
        // pas aux enfants imbriqués (NewsletterNode sans id propre).
        const currentAsNewsletter = this.current as Newsletter;
        const isRootSubscription = !!currentAsNewsletter?.id;

        if (isRootSubscription && this.isAdmin) {
            otherItems.push({
                label: this.translate.instant('subscriptions.managePartners'),
                icon: "fas fa-share-alt",
                permission: "SUBSCRIPTIONS.edit",
                command: () => {
                    this.openPartnersSetting(currentAsNewsletter);
                },
            });
        }

        if (isRootSubscription && this.isRecipient(currentAsNewsletter)) {
            otherItems.push({
                label: this.translate.instant('subscriptions.duplicateInScope'),
                icon: "fas fa-clone",
                permission: "SUBSCRIPTIONS.edit",
                command: () => {
                    this.duplicateSubscription(currentAsNewsletter);
                },
            });
        }

        return [
            {
                label: this.translate.instant('common.edition'),
                items: [
                    {
                        label: this.translate.instant('subscriptions.edit'),
                        icon: "fas fa-pen",
                        permission: "SUBSCRIPTIONS.edit",
                        command: () => {
                            this.editable = this.current;
                        },
                    },
                    {
                        label: this.translate.instant('subscriptions.exportJson'),
                        icon: "fas fa-download",
                        permission: "SUBSCRIPTIONS.edit",
                        command: () => {
                            const subscription = this.searchSubscriptionDeep(
                                this.subscriptions,
                                this.current.uuid
                            );
                            if (subscription) {
                                this.exportNewsletterJson({ node: subscription });
                            }
                        },
                    },
                    {
                        label: this.translate.instant('subscriptions.delete'),
                        icon: "fas fa-trash",
                        permission: "SUBSCRIPTIONS.delete",
                        command: () => {
                            this.remove(this.current);
                        },
                    },
                ],
            },
            {
                label: this.translate.instant('subscriptions.otherGroup'),
                items: otherItems,
            },
        ];
    }
    updateMessage = {
        severity: "success",
        summary: this.translate.instant('subscriptions.updatedSummary'),
        detail: this.translate.instant('subscriptions.updatedDetail'),
    };

    lang = "FR";

    constructor(
        private configuratorNewsletterService: ConfiguratorNewsletterService,
        private clipboardService: ClipboardService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private readonly translate: TranslateService,
        private accountService: AccountService
    ) {
        this.initExportMenu();
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            { label: this.translate.instant('menu.marketing.label') },
            { label: this.translate.instant('menu.marketing.subscriptions') }
        ];
        this.description = this.translate.instant('subscriptions.description');
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    EMPTY_FUNCTION = () => {};

    EDITABLE_NULL = () => {
        this.editable = null;
    };

    searchSubscriptionDeep(
        subscriptions: Subscription[],
        uuid: string
    ): Subscription {
        for (const subscription of subscriptions) {
            if (subscription.data.uuid === uuid) {
                return subscription;
            }
            const deep = this.searchSubscriptionDeep(
                subscription.children,
                uuid
            );
            if (deep) {
                return deep;
            }
        }
        return undefined;
    }

    isParent(subscription: Subscription, uuid: string) {
        if (!subscription) {
            return undefined;
        }
        return !!this.searchSubscriptionDeep(subscription.children, uuid);
    }

    datastoreChange(datastore: DatastoreDto) {
        this.datastore = datastore;
        if (this.datastore) {
            this.accountService.fetch().subscribe((account) => {
                this.isAdmin = isAdminOfTenant(account, this.datastore.code);
            });
        } else {
            this.isAdmin = false;
        }
    }

    isRecipient(subscription: Newsletter): boolean {
        return !!(this.partner && subscription?.idPart && subscription.idPart !== this.partner.code);
    }

    openPartnersSetting(subscription: Newsletter) {
        this.sharingTarget = subscription;
        this.affectedPartnersSettings.selectedAffectedPartners = subscription.affectedPartners ? [...subscription.affectedPartners] : [];
        this.affectedPartnersSettings.openSetting();
    }

    updateAffectedPartners(affectedPartners: string[]) {
        if (!this.sharingTarget || !this.sharingTarget.id) {
            return;
        }
        this.configuratorNewsletterService
            .updateNewsletterPartnersUsingPUT(this.datastore.code, this.lang, this.sharingTarget.id, affectedPartners, this.lang)
            .subscribe(() => {
                this.messageService.add({
                    severity: "success",
                    summary: this.translate.instant('subscriptions.updatedSummary'),
                    detail: this.translate.instant('subscriptions.partnersUpdatedDetail'),
                });
                this.loadAll();
            });
    }

    duplicateSubscription(subscription: Newsletter) {
        if (!subscription?.id || !this.partner) {
            return;
        }
        this.configuratorNewsletterService
            .duplicateNewsletterUsingPOST(this.datastore.code, this.lang, this.partner.code, subscription.id, this.lang)
            .subscribe(() => {
                this.messageService.add({
                    severity: "success",
                    summary: this.translate.instant('subscriptions.duplicatedSummary'),
                    detail: this.translate.instant('subscriptions.duplicatedDetail'),
                });
                this.loadAll();
            });
    }

    partnerChange(partner: Partner) {
        this.partner = partner;
        this.loadAll();
    }

    langChange(lang: string) {
        this.lang = lang;
        this.loadAll();
    }

    parseSubscriptions(
        subscriptions: NewsletterNode[],
        parent?: Subscription
    ): Subscription[] {
        return subscriptions.map((newletter) => {
            const subscription = {
                data: newletter,
                children: [],
                expanded: false,
                parent,
            };
            subscription.children = this.parseSubscriptions(
                JSON.parse(JSON.stringify(newletter)).children,
                subscription
            );
            subscription.expanded = !!(
                this.currentOpened &&
                this.isParent(subscription, this.currentOpened.data.uuid)
            );
            return subscription;
        });
    }

    parseNewletter(subscriptions: Subscription[]): Newsletter[] {
        return subscriptions.map((subscription) => {
            const result = this.transformSubscriptionToNewsletter(subscription);

            return result;
        });
    }


    private transformSubscriptionToNewsletter(subscription: Subscription) {
        const result = {
            ...subscription.data,
            children: [],
            idPart: this.partner.code,
        };
        subscription.children?.forEach(c => {
            result.children.push(this.transformSubscriptionToNewsletter(c));
        });
        return result;
    }

    loadAll() {
        if (this.datastore && this.partner) {
            return new Promise((resolve) =>
                this.configuratorNewsletterService
                    .getNewslettersUsingGET(
                        this.datastore.code,
                        this.lang,
                        this.lang,
                        this.partner.code
                    )
                    .subscribe((res) => {
                        this.subscriptions = this.parseSubscriptions(
                            res as Newsletter[]
                        );
                        this.updateExportMenuState();
                        resolve(res);
                    })
            );
        } else {
            this.subscriptions = [];
            this.updateExportMenuState();
            return new Promise((resolve) => resolve([]));
        }
    }

    save(message: ToastMessageOptions = null) {
        return new Promise((resolve) => {
            const isNew = this.editable.id ? false : true;
            const sub = this.parseNewletter(this.subscriptions);
            this.configuratorNewsletterService
                .updateNewsletterUsingPUT(
                    this.datastore.code,
                    this.lang,
                    sub,
                    isNew,
                    this.lang
                )
                .subscribe(
                    () => {
                        this.loadAll()
                            .then(() => {
                                if (message) {
                                    this.messageService.add(message);
                                }
                            })
                            .then(resolve);
                    },
                    (e) => {
                        console.log("hello world", e);
                        if (message) {
                            this.messageService.add({
                                severity: "error",
                                summary: this.translate.instant('subscriptions.errorSummary'),
                                detail: this.translate.instant('subscriptions.inputErrorDetail'),
                            });
                        }
                    }
                );
    });
    }

    toggleChannel(subscription: Newsletter, element: "sms" | "email") {
        if (subscription.channelEnabled.includes(element)) {
            subscription.channelEnabled = subscription.channelEnabled.filter(
                (el) => el !== element
            );
        } else {
            subscription.channelEnabled = [
                ...subscription.channelEnabled,
                element,
            ];
        }
    }

    async createSubscription() {
        const subscription = {
            children: [],
            data: {
                uuid: null,
                name: "",
                description: "",
                channelEnabled: [],
                children: [],
            },
            expanded: true,
            parent: undefined,
        };
        this.subscriptions = [...this.subscriptions, subscription];
        this.editable = subscription.data as Newsletter;
    }

    filterRemove(subscriptions: Subscription[], subscriptionUUID: string) {
        return subscriptions
            .filter((el) => el.data.uuid !== subscriptionUUID)
            .map((el) => {
                // el.children = el.children.filter((el) => el.data.uuid !== subscriptionUUID)
                // el.data.children = el.data.children.filter((el) => el.uuid !== subscriptionUUID)
                el.children = this.filterRemove(el.children, subscriptionUUID);
                el.data.children = this.filterRemoveNode(
                    el.data.children,
                    subscriptionUUID
                );
                return el;
            });
    }

    filterRemoveNode(
        subscriptions: NewsletterNode[],
        subscriptionUUID: string
    ) {
        return subscriptions
            .filter((el) => el.uuid !== subscriptionUUID)
            .map((el) => {
                el.children = this.filterRemoveNode(
                    el.children,
                    subscriptionUUID
                );
                return el;
            });
    }

    remove(newletter: Newsletter) {
        this.confirmationService.confirm({
            message: this.translate.instant('subscriptions.confirmDelete', {name: newletter.name}),
            accept: () => {
                let isChild = false;
                this.currentOpened = this.searchSubscriptionDeep(
                    this.subscriptions,
                    newletter.uuid
                )?.parent;
                if (this.currentOpened) {
                    isChild = true;
                }
                this.subscriptions = this.filterRemove(
                    this.subscriptions,
                    newletter.uuid
                );
                if (!isChild) {
                    this.configuratorNewsletterService
                    .deleteNewsletterUsingDELETE(
                        this.datastore.code,
                        newletter.uuid,
                        true
                    )
                    .subscribe(() => {
                        this.save().catch(this.EMPTY_FUNCTION);
                        this.messageService.add({
                            severity: "success",
                            summary: this.translate.instant("subscriptions.deletedSummary"),
                            detail: this.translate.instant("subscriptions.deletedDetail"),
                        });
                    });
                }
                else {
                    const sub = this.parseNewletter(this.subscriptions);
                    this.configuratorNewsletterService
                        .updateNewsletterUsingPUT(
                            this.datastore.code,
                            this.lang,
                            sub,
                            false,
                            this.lang
                        )
                        .subscribe(() => {
                            this.save().catch(this.EMPTY_FUNCTION);
                            this.messageService.add({
                                severity: "success",
                                summary: this.translate.instant("subscriptions.deletedSummary"),
                                detail: this.translate.instant("subscriptions.deletedDetail"),
                            });
                        });
                }
            }
        });
    }

    removeChildren(parentNewsletter: Newsletter, subscriptionUUID: string) {
        parentNewsletter.children = parentNewsletter.children.filter(
            (el) => el.uuid !== subscriptionUUID
        );
        parentNewsletter.children.forEach((child) => {
            this.removeChildren(child, subscriptionUUID);
        });
        return parentNewsletter;
    }

    // ============ EXPORT/IMPORT METHODS ============

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: this.translate.instant('subscriptions.importFile'),
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: this.translate.instant('subscriptions.exportSelection'),
                icon: 'fas fa-file-export',
                command: () => this.exportSelectedNewsletters(),
                disabled: !this.selectedSubscriptions || this.selectedSubscriptions.length === 0
            },
            {
                label: this.translate.instant('subscriptions.exportAll'),
                icon: 'fas fa-download',
                command: () => this.exportAllNewsletters(),
                disabled: !this.subscriptions || this.subscriptions.length === 0
            }
        ];
    }

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.newsletterFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('subscriptions.errorSummary'),
                detail: this.translate.instant('subscriptions.unsupportedFormatDetail')
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.configuratorNewsletterService.importNewsletterJson(this.datastore.code, jsonContent, this.lang).subscribe(
                (newsletter: Newsletter) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('subscriptions.successSummary'),
                        detail: this.translate.instant('subscriptions.importedOneDetail', {name: newsletter.name})
                    });
                    this.loadAll();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('subscriptions.errorSummary'),
                        detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.configuratorNewsletterService.importNewslettersZip(this.datastore.code, file, this.lang).subscribe(
            (newsletters: Newsletter[]) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('subscriptions.successSummary'),
                    detail: this.translate.instant('subscriptions.importedManyDetail', {n: newsletters.length})
                });
                this.loadAll();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('subscriptions.errorSummary'),
                    detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte une seule newsletter en JSON
     */
    exportNewsletterJson(rowNode: any) {
        const subscription = rowNode.node as Subscription;
        const newsletter = subscription.data as Newsletter;
        if (!newsletter.id) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('subscriptions.warnSummary'),
                detail: this.translate.instant('subscriptions.notSavedDetail')
            });
            return;
        }

        this.configuratorNewsletterService.exportNewsletterJson(this.datastore.code, newsletter.id, this.lang).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `newsletter-${newsletter.name}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('subscriptions.successSummary'),
                    detail: this.translate.instant('subscriptions.exportedOneDetail')
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('subscriptions.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Collecte tous les IDs de newsletters de manière récursive
     */
    private collectAllNewsletterIds(subscriptions: Subscription[]): string[] {
        const ids: string[] = [];
        subscriptions.forEach(sub => {
            const newsletter = sub.data as Newsletter;
            if (newsletter.id) {
                ids.push(newsletter.id);
            }
            if (sub.children && sub.children.length > 0) {
                ids.push(...this.collectAllNewsletterIds(sub.children));
            }
        });
        return ids;
    }

    /**
     * Exporte plusieurs newsletters en ZIP
     */
    exportSelectedNewsletters() {
        if (!this.selectedSubscriptions || this.selectedSubscriptions.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('subscriptions.warnSummary'),
                detail: this.translate.instant('subscriptions.noneSelectedDetail')
            });
            return;
        }

        const ids = this.collectAllNewsletterIds(this.selectedSubscriptions);
        if (ids.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('subscriptions.warnSummary'),
                detail: this.translate.instant('subscriptions.noneValidDetail')
            });
            return;
        }

        this.configuratorNewsletterService.exportNewslettersZip(this.datastore.code, ids, this.lang).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `newsletters-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('subscriptions.successSummary'),
                    detail: this.translate.instant('subscriptions.exportedManyDetail', {n: ids.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('subscriptions.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte toutes les newsletters en ZIP
     */
    exportAllNewsletters() {
        if (!this.subscriptions || this.subscriptions.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('subscriptions.warnSummary'),
                detail: this.translate.instant('subscriptions.noneToExportDetail')
            });
            return;
        }

        const ids = this.collectAllNewsletterIds(this.subscriptions);
        if (ids.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('subscriptions.warnSummary'),
                detail: this.translate.instant('subscriptions.noneValidDetail')
            });
            return;
        }

        this.configuratorNewsletterService.exportNewslettersZip(this.datastore.code, ids, this.lang).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `newsletters-export-all.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('subscriptions.successSummary'),
                    detail: this.translate.instant('subscriptions.exportedManyDetail', {n: ids.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('subscriptions.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Met à jour l'état des items du menu export
     */
    private updateExportMenuState() {
        if (this.exportMenuItems && this.exportMenuItems.length > 0) {
            // Update "Exporter la sélection" (index 1)
            if (this.exportMenuItems[1]) {
                this.exportMenuItems[1].disabled = !this.selectedSubscriptions || this.selectedSubscriptions.length === 0;
            }
            // Update "Exporter tout" (index 2)
            if (this.exportMenuItems[2]) {
                this.exportMenuItems[2].disabled = !this.subscriptions || this.subscriptions.length === 0;
            }
        }
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }
}
